/**
 * The **reference-data** read surface: the controlled values a form needs in
 * order to be filled in at all - `GET /api/users/selectable` and
 * `GET /api/master-data/:set/selectable`.
 *
 * ## Why this is separate from the administration surface
 *
 * `admin.ts` already describes `/api/admin/master-data/:set`, and it would look
 * like the natural source for the intake form's dropdowns. It cannot be: every
 * route under `/api/admin/master-data` requires the `masterdata:admin`
 * capability, which `@complan/domain`'s capability table grants to
 * `system_administrator` **only**. The role that actually fills in an Initiative,
 * `commercial_planning`, holds `masterdata:write` and not `masterdata:admin`, so
 * an intake user reading the administration list is denied at the capability layer
 * (R2.5) before the handler runs.
 *
 * The two surfaces also answer different questions. Administration asks "what
 * records exist, including deactivated and future-dated ones, so I can maintain
 * them" (R16.1-R16.3). A form asks "what may I select *now*" - R16.5's
 * active-and-effective subset, which is narrower and is deliberately not the same
 * list. Serving both from one route would mean one capability for two audiences
 * and a query flag deciding which rules apply.
 *
 * ## Owners are users, not master data
 *
 * An Initiative's Owner is a `complan.app_user` row - someone the
 * Identity_Service has authenticated - not a maintainable value set. R4.10 says
 * the Validation_Engine "SHALL accept only a user account that the
 * Identity_Service reports as active at the time of the assignment", so the
 * question this surface answers for owners is exactly "which accounts are active
 * right now". There is no create shape here and there is none anywhere else
 * either: accounts arrive by signing in, and ComPlan never invents one.
 */
import { z } from 'zod';
import { masterDataSetSchema } from './enums.js';
import { idSchema, listFilterSchema } from './primitives.js';
/**
 * One account that may be assigned as an Owner, Backup Owner or dependency owner.
 *
 * Carries display identity and nothing more. There is no role set, no capability
 * list and no scope here: what a user is *permitted* to do is resolved per request
 * from the database (`GET /api/me` for the acting user, RLS for everything else),
 * and an owner needs no role at all - R4.10 requires only an active account. A
 * role set on this shape would be both unnecessary and a wider disclosure than
 * assigning an owner calls for.
 *
 * `email` is included because display names collide in a real directory and the
 * person picking an owner needs to tell two colleagues apart. It is the same
 * address already visible on any Initiative that names this user as Owner.
 */
export const selectableUserSchema = z.object({
    id: idSchema,
    displayName: z.string().min(1),
    email: z.string().min(1),
    /**
     * `false` only for an account named in `includeIds` that has since been
     * deactivated - the R4.11 case. The active list never contains an inactive
     * account, so a `false` here always means "retained because the record under
     * edit still references it", which is what the client marks with the R5.9
     * inactive indicator.
     */
    isActive: z.boolean(),
});
/**
 * `GET /api/users/selectable`.
 *
 * `includeIds` names accounts to return **even if they are inactive**. This is
 * what makes R4.11 satisfiable in a form: an Initiative whose Owner was
 * deactivated after assignment must still render that Owner - marked inactive -
 * so the record can be re-saved without silently dropping the assignment, and so
 * the user can see who they are replacing. Without it the select would simply
 * omit the stored value and the field would look empty.
 *
 * Comma-separated or repeated, via {@link listFilterSchema}, so the same shape
 * reads `?includeIds=a,b` and `?includeIds=a&includeIds=b`.
 */
export const selectableUsersQuerySchema = z.object({
    includeIds: listFilterSchema(idSchema),
});
/**
 * `GET /api/users/selectable` response.
 *
 * Unpaged, deliberately. The list backs a `<select>` that has to hold every
 * assignable owner at once - a page of it would silently hide valid choices, and a
 * user cannot pick what is not offered. ComPlan's population is one commercial
 * organisation's planners and stakeholders, so the list is small enough that
 * paging would cost correctness for no benefit. If that ceases to be true, the
 * right change is a typeahead endpoint that takes a search term, not a page
 * window on this one.
 */
export const selectableUsersResponseSchema = z.object({
    items: z.array(selectableUserSchema).default([]),
});
/**
 * `GET /api/master-data/:set/selectable` path parameters.
 *
 * The same nine sets `masterDataSetSchema` names; this surface adds no set of its
 * own, it only narrows which *records* of a set are offered.
 */
export const selectableMasterDataParamsSchema = z.object({ set: masterDataSetSchema });
/**
 * `GET /api/master-data/:set/selectable`.
 *
 * `includeIds` serves R5.9 exactly as it does for users above: a value the record
 * under edit already references stays selectable even after deactivation, marked
 * inactive, so re-saving an existing Initiative cannot quietly drop it.
 */
export const selectableMasterDataQuerySchema = z.object({
    includeIds: listFilterSchema(idSchema),
});
//# sourceMappingURL=reference.js.map